/**
 * AdminOrganizationsTab Component
 *
 * Admin interface for managing organizations.
 * Platform admins can view and manage all organizations.
 */

import React, { useState, useCallback, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Search, RefreshCw } from 'lucide-react';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogCancel,
  AlertDialogAction,
} from '@/components/ui/alert-dialog';
import { useOrganizationStore, Organization } from '@/stores/organization/organization-store';
import { OrganizationCard } from './OrganizationCard';
import { EditOrganizationDialog } from './EditOrganizationDialog';
import * as errorUtils from '@/utils/errors';

export const AdminOrganizationsTab: React.FC = () => {
  const {
    organizations,
    allOrganizations,
    platformAdminEntitlement,
    fetchOrganizations,
    fetchAllOrganizations,
    deleteOrganization,
    isLoading,
    error,
  } = useOrganizationStore();

  // Dialog states
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [selectedOrg, setSelectedOrg] = useState<Organization | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [deleteTarget, setDeleteTarget] = useState<Organization | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  // Determine which organizations to display
  const displayOrganizations = platformAdminEntitlement && allOrganizations.length > 0
    ? allOrganizations
    : organizations;

  // Filter organizations based on search query
  const filteredOrganizations = displayOrganizations.filter((org) => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      org.name.toLowerCase().includes(query) ||
      org.slug.toLowerCase().includes(query) ||
      org.description?.toLowerCase().includes(query)
    );
  });

  // Fetch organizations on mount
  useEffect(() => {
    fetchOrganizations().catch((error) => {
      console.error('Failed to fetch organizations:', error);
    });
    if (platformAdminEntitlement) {
      fetchAllOrganizations().catch((error) => {
        console.error('Failed to fetch all organizations:', error);
      });
    }
  }, [fetchOrganizations, fetchAllOrganizations, platformAdminEntitlement]);

  // Handle refresh
  const handleRefresh = useCallback(async () => {
    try {
      await fetchOrganizations();
      if (platformAdminEntitlement) {
        await fetchAllOrganizations();
      }
    } catch (error) {
      console.error('Failed to refresh organizations:', error);
    }
  }, [fetchOrganizations, fetchAllOrganizations, platformAdminEntitlement]);

  // Handle edit organization
  const handleEditOrg = useCallback((org: Organization) => {
    setSelectedOrg(org);
    setShowEditDialog(true);
  }, []);

  // Handle delete organization
  const handleDeleteOrg = useCallback((org: Organization) => {
    setDeleteTarget(org);
    setDeleteError(null);
  }, []);

  const confirmDeleteOrg = useCallback(async () => {
    if (!deleteTarget) return;
    try {
      await deleteOrganization(deleteTarget.slug);
      setDeleteTarget(null);
      await handleRefresh();
    } catch (err: unknown) {
      const errorMessage =
        errorUtils.getErrorMessageOrFallback(err, 'Failed to delete organization');
      setDeleteError(errorMessage);
    }
  }, [deleteTarget, deleteOrganization, handleRefresh]);

  // Handle edit success
  const handleEditSuccess = useCallback(() => {
    handleRefresh();
    setSelectedOrg(null);
  }, [handleRefresh]);

  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div className="flex-shrink-0 p-4 border-b border-border">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-xl font-semibold">Organizations</h2>
            <p className="text-sm text-muted-foreground">
              {platformAdminEntitlement
                ? 'Manage all organizations in the platform.'
                : 'Manage your organizations.'}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={handleRefresh} disabled={isLoading}>
              <RefreshCw className={`h-4 w-4 mr-2 ${isLoading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>
        </div>

        {/* Search */}
        <div className="mt-4 relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search organizations..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
          />
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-4">
        {/* Loading state */}
        {isLoading && (
          <div className="flex items-center justify-center h-40">
            <div className="text-muted-foreground">Loading organizations...</div>
          </div>
        )}

        {/* Error state */}
        {error && !isLoading && (
          <div className="p-4 rounded-lg bg-destructive/10 text-destructive">
            <div className="font-medium">Error loading organizations</div>
            <div className="text-sm mt-1">{error}</div>
            <Button variant="outline" size="sm" className="mt-2" onClick={handleRefresh}>
              Retry
            </Button>
          </div>
        )}

        {/* Empty state */}
        {!isLoading && !error && filteredOrganizations.length === 0 && (
          <div className="flex flex-col items-center justify-center h-40 text-center">
            <div className="text-4xl mb-4">🏢</div>
            <div className="text-lg font-medium">
              {searchQuery ? 'No organizations found' : 'No organizations yet'}
            </div>
            <div className="text-sm text-muted-foreground mb-4">
              {searchQuery
                ? 'Try adjusting your search query.'
                : 'Organizations will appear here when created.'}
            </div>
          </div>
        )}

        {/* Organization list */}
        {!isLoading && !error && filteredOrganizations.length > 0 && (
          <div className="space-y-4">
            {filteredOrganizations.map((org) => (
              <OrganizationCard
                key={org._id}
                organization={org}
                onEdit={() => handleEditOrg(org)}
                onDelete={() => handleDeleteOrg(org)}
                canGrantInternalPlan={platformAdminEntitlement}
                onInternalPlanGranted={handleRefresh}
              />
            ))}
          </div>
        )}
      </div>

      {/* Edit Dialog */}
      <EditOrganizationDialog
        open={showEditDialog}
        onOpenChange={setShowEditDialog}
        organization={selectedOrg}
        onSuccess={handleEditSuccess}
      />

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteTarget !== null} onOpenChange={(open) => { if (!open) { setDeleteTarget(null); setDeleteError(null); } }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete organization</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete &quot;{deleteTarget?.name}&quot;? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          {deleteError && (
            <div className="p-3 rounded-lg bg-destructive/10 text-destructive text-sm">
              {deleteError}
            </div>
          )}
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={confirmDeleteOrg}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default AdminOrganizationsTab;
